import { Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import {
  Building2, Check, CircleUser as UserCircle, EyeOff, StickyNote, Plus, LogOut, Settings
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useMyCompanyLinks } from '../../hooks/useMyCompanyLinks';
import { useCanOpen } from '../../hooks/useMyAccess';
import { useOrganizationProfile } from '../../hooks/useOrganization';
import { linksToOtherCompanies } from '../../lib/companyLinks';
import { isPresentationMode, setPresentationMode } from '../../lib/presentationMode';

export function UserDropdown() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  // The notes and the settings are an administrator's screens until the base opens them to a member (lot L6g).
  const canOpen = useCanOpen();
  // « Mes sociétés » (lot BS): the person's other companies, each its own application at its own address.
  const { state: companies } = useMyCompanyLinks();
  const company = useOrganizationProfile();
  const otherCompanies = companies.status === 'ready' ? linksToOtherCompanies(companies.links, window.location.origin) : [];

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleNewNote = () => {
    navigate('/admin/notes?new=1');
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center space-x-2 rounded-lg px-2 py-1.5 hover:bg-neutral-100 transition-colors">
        <div className="w-8 h-8 bg-gradient-to-br from-[#0066FF] to-[#00AAFF] rounded-full flex items-center justify-center text-white text-sm font-semibold overflow-hidden flex-shrink-0">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            user?.display_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'
          )}
        </div>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-50 py-1">
          {/* User card */}
          <div className="px-4 py-3 border-b border-neutral-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#0066FF] to-[#00AAFF] rounded-full flex items-center justify-center text-white text-sm font-semibold overflow-hidden flex-shrink-0">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  user?.display_name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">
                  {user?.display_name || 'Utilisateur'}
                </p>
                <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {otherCompanies.length > 0 && (
            <div className="border-b border-neutral-100 py-1">
              <p className="px-4 pb-1 pt-2 text-xs font-medium text-neutral-500">Mes sociétés</p>
              {company && (
                <p className="flex items-center px-4 py-2 text-sm text-neutral-900" aria-current="true">
                  <Check className="h-4 w-4 mr-3 text-neutral-400" />
                  <span className="truncate">{company.name}</span>
                </p>
              )}
              {/* Another company is another cell, at its own address: the whole page goes there (PLAN-MULTI-TENANT § 4.3). */}
              {otherCompanies.map((link) => (
                <Menu.Item key={link.id}>
                  {({ active }) => (
                    <a
                      href={link.url}
                      className={`flex items-center px-4 py-2 text-sm ${active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700'}`}
                    >
                      <Building2 className="h-4 w-4 mr-3 text-neutral-400" />
                      <span className="truncate">{link.label}</span>
                    </a>
                  )}
                </Menu.Item>
              ))}
            </div>
          )}

          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/admin/profile"
                  className={`flex items-center px-4 py-2 text-sm ${active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700'}`}
                >
                  <UserCircle className="h-4 w-4 mr-3 text-neutral-400" />
                  Mon Profil
                </Link>
              )}
            </Menu.Item>
            {canOpen('/admin/notes') && (
              <>
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/admin/notes"
                      className={`flex items-center px-4 py-2 text-sm ${active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700'}`}
                    >
                      <StickyNote className="h-4 w-4 mr-3 text-neutral-400" />
                      Mes Notes
                    </Link>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleNewNote}
                      className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700'}`}
                    >
                      <Plus className="h-4 w-4 mr-3 text-neutral-400" />
                      Nouvelle Note
                      <kbd className="ml-auto text-[10px] text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200">
                        Ctrl+Shift+N
                      </kbd>
                    </button>
                  )}
                </Menu.Item>
              </>
            )}
            {/* The gear left the header with the Bureau (PLAN-BUREAU lot 3): Réglages are here and in « Outils ». */}
            {canOpen('/admin/settings') && (
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/admin/settings"
                    className={`flex items-center px-4 py-2 text-sm ${active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700'}`}
                  >
                    <Settings className="h-4 w-4 mr-3 text-neutral-400" />
                    Réglages
                  </Link>
                )}
              </Menu.Item>
            )}
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => setPresentationMode(!isPresentationMode())}
                  className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700'}`}
                >
                  <EyeOff className="h-4 w-4 mr-3 text-neutral-400" />
                  {isPresentationMode() ? 'Quitter le mode présentation' : 'Mode présentation'}
                </button>
              )}
            </Menu.Item>
          </div>

          <div className="border-t border-neutral-100 py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleSignOut}
                  className={`flex items-center w-full px-4 py-2 text-sm ${active ? 'bg-red-50 text-red-700' : 'text-neutral-700'}`}
                >
                  <LogOut className="h-4 w-4 mr-3 text-neutral-400" />
                  Se déconnecter
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
